import React, { useState, useMemo } from 'react';
import { 
  MagnifyingGlassIcon, 
  ListBulletIcon, 
  Squares2X2Icon,
  ArrowDownTrayIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const PremiumTable = ({ 
  columns = [], 
  data = [], 
  searchKeys = [], 
  title = "Data Table", 
  primaryKey = "id",
  paginationMode = "standard", // 'standard' | 'infinite' | 'none'
  itemsPerPage = 10
}) => {
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'card'
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showExportMenu, setShowExportMenu] = useState(false);

  // 1. Filter Data
  const filteredData = useMemo(() => {
    if (!searchQuery) return data;
    const lowerQuery = searchQuery.toLowerCase();
    
    return data.filter(item => {
      return searchKeys.some(key => {
        // Handle nested keys e.g. "business.name"
        const keys = key.split('.');
        let val = item;
        for (const k of keys) {
          if (val) val = val[k];
        }
        return String(val).toLowerCase().includes(lowerQuery);
      });
    });
  }, [data, searchQuery, searchKeys]);

  // 2. Paginate Data
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  
  const displayedData = useMemo(() => {
    if (paginationMode === 'none') return filteredData;
    
    if (paginationMode === 'infinite') {
      // In infinite mode, page limits how much is shown from 0 to current limit
      return filteredData.slice(0, currentPage * itemsPerPage);
    }
    
    // Standard pagination
    const startIdx = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(startIdx, startIdx + itemsPerPage);
  }, [filteredData, currentPage, itemsPerPage, paginationMode]);

  // Reset pagination on search
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Export Logic
  const handleExportCSV = () => {
    if (!filteredData.length) return;
    const exportCols = columns.filter(c => c.header && !c.header.toLowerCase().includes('action'));
    const headers = exportCols.map(c => c.header).join(',');
    const rows = filteredData.map(row => {
      return exportCols.map(c => {
        let val = '';
        if (c.accessor) {
          val = row;
          const keys = c.accessor.split('.');
          for (const k of keys) val = val ? val[k] : '';
        }
        const escaped = ('' + (val ?? '')).replace(/"/g, '""');
        return `"${escaped}"`;
      }).join(',');
    });
    
    const csvContent = [headers, ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    downloadBlob(blob, `${title.toLowerCase().replace(/\s+/g, '_')}.csv`);
    setShowExportMenu(false);
  };

  const handleExportJSON = () => {
    const jsonString = JSON.stringify(filteredData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    downloadBlob(blob, `${title.toLowerCase().replace(/\s+/g, '_')}.json`);
    setShowExportMenu(false);
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    
    const exportCols = columns.filter(c => c.header && !c.header.toLowerCase().includes('action'));
    const tableColumns = exportCols.map(c => c.header);
    const tableRows = filteredData.map(row => {
      return exportCols.map(c => {
        let val = '';
        if (c.accessor) {
          val = row;
          const keys = c.accessor.split('.');
          for (const k of keys) val = val ? val[k] : '';
        }
        return String(val ?? '');
      });
    });

    autoTable(doc, {
      head: [tableColumns],
      body: tableRows,
      theme: 'grid',
      styles: { fontSize: 8 },
      headStyles: { fillColor: [220, 38, 38] } // Red Khatabook brand
    });

    doc.save(`${title.toLowerCase().replace(/\s+/g, '_')}.pdf`);
    setShowExportMenu(false);
  };

  const downloadBlob = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Render Helpers
  const renderCell = (col, row) => {
    if (col.render) return col.render(row);
    let val = row;
    if (col.accessor) {
      const keys = col.accessor.split('.');
      for (const k of keys) val = val ? val[k] : '';
    }
    return val;
  };

  return (
    <div className="bg-white rounded-[20px] shadow-[0_8px_30px_rgb(0,0,0,0.08)] overflow-visible flex flex-col font-sans border-t-[6px] border-red-600 transition-all">
      
      {/* Premium Header Toolbar */}
      <div className="px-6 py-6 border-b border-gray-100 flex flex-col lg:flex-row lg:items-center justify-between gap-6 bg-gradient-to-b from-gray-50/50 to-white rounded-t-[14px] z-20">
        {title ? (
          <div>
            <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight">{title}</h2>
            <p className="text-sm font-medium text-gray-500 mt-1">Manage and organize your platform data seamlessly.</p>
          </div>
        ) : (
          <div></div>
        )}
        
        <div className="flex flex-wrap items-center gap-4 w-full lg:w-auto">
          {/* Pill-shaped Search */}
          {searchKeys.length > 0 && (
            <div className="relative flex-1 sm:min-w-[280px] group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors group-focus-within:text-red-500">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 group-focus-within:text-red-500 transition-colors" />
              </div>
              <input
                type="text"
                placeholder="Search anything..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-full pl-11 pr-4 py-2.5 border-0 ring-1 ring-inset ring-gray-200 rounded-full text-sm font-medium bg-gray-50/50 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-red-500 focus:bg-white transition-all shadow-sm hover:shadow-md"
              />
            </div>
          )}

          {/* Pill-shaped View Toggles */}
          <div className="flex items-center bg-gray-100/80 rounded-full p-1 ring-1 ring-inset ring-gray-200/50 shadow-inner">
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded-full transition-all duration-300 ${viewMode === 'table' ? 'bg-white shadow-md text-red-600 scale-105' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-200/50'}`}
              title="Table View"
            >
              <ListBulletIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('card')}
              className={`p-2 rounded-full transition-all duration-300 ${viewMode === 'card' ? 'bg-white shadow-md text-red-600 scale-105' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-200/50'}`}
              title="Card View"
            >
              <Squares2X2Icon className="h-4 w-4" />
            </button>
          </div>

          {/* Premium Export Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-full hover:bg-gray-50 hover:border-gray-300 hover:text-red-600 text-sm font-semibold transition-all shadow-sm hover:shadow-md"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Export</span>
            </button>
            
            {showExportMenu && (
              <div className="absolute right-0 mt-3 w-44 rounded-2xl shadow-[0_20px_50px_-12px_rgba(0,0,0,0.25)] border border-gray-100 bg-white/90 backdrop-blur-md z-50 overflow-hidden transform opacity-100 scale-100 transition-all duration-200 origin-top-right">
                <div className="py-2" role="menu">
                  <button onClick={handleExportPDF} className="w-full text-left block px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors">📄 PDF Document</button>
                  <button onClick={handleExportCSV} className="w-full text-left block px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors">📊 CSV Format</button>
                  <button onClick={handleExportJSON} className="w-full text-left block px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors">🗄️ JSON Data</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="relative z-10 flex-1 bg-white">
        {displayedData.length === 0 ? (
          <div className="py-24 text-center">
            <div className="mx-auto h-20 w-20 bg-gray-50/80 rounded-full flex items-center justify-center mb-5 ring-1 ring-gray-100 shadow-inner">
              <MagnifyingGlassIcon className="h-10 w-10 text-gray-300" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">No records found</h3>
            <p className="mt-2 text-sm text-gray-500 max-w-sm mx-auto font-medium">We couldn't find anything matching your search criteria. Try adjusting your filters.</p>
          </div>
        ) : (
          <>
            {/* TABLE VIEW */}
            {viewMode === 'table' && (
              <div className="overflow-x-auto w-full">
                <table className="min-w-full text-left border-collapse">
                  <thead>
                    <tr>
                      {columns.map((col, idx) => (
                        <th 
                          key={idx}
                          className="px-6 py-4 bg-gray-50/80 border-b border-gray-200 text-xs font-bold text-gray-500 uppercase tracking-widest sticky top-0 backdrop-blur-sm"
                        >
                          {col.header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100/80">
                    {displayedData.map((row) => (
                      <tr key={row[primaryKey]} className="hover:bg-red-50/40 hover:shadow-[inset_4px_0_0_0_#dc2626] transition-all duration-200 cursor-default bg-white">
                        {columns.map((col, idx) => (
                          <td key={idx} className="px-6 py-5 whitespace-nowrap text-sm text-gray-900">
                            {renderCell(col, row)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* CARD VIEW */}
            {viewMode === 'card' && (
              <div className="p-6 md:p-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 bg-gray-50/50">
                {displayedData.map((row) => (
                  <div key={row[primaryKey]} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:border-red-300 hover:shadow-[0_8px_30px_rgb(220,38,38,0.12)] hover:-translate-y-1 transition-all duration-300 relative overflow-hidden group">
                    <div className="absolute top-0 left-0 w-1 h-full bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    {columns.map((col, idx) => (
                      <div key={idx} className={`mb-4 last:mb-0 ${col.header === 'Actions' ? 'mt-6 pt-5 border-t border-gray-100 flex justify-end' : ''}`}>
                        {col.header !== 'Actions' && (
                          <p className="text-[11px] font-extrabold text-gray-400 uppercase tracking-widest mb-1.5">{col.header}</p>
                        )}
                        <div className="text-sm text-gray-900 whitespace-normal">
                          {renderCell(col, row)}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Pagination Footer */}
      {filteredData.length > 0 && paginationMode !== 'none' && (
        <div className="px-6 py-5 border-t border-gray-100 bg-gray-50/30 flex items-center justify-between rounded-b-[20px] z-20">
          
          {paginationMode === 'standard' && (
            <>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Showing <span className="font-bold text-gray-900">{((currentPage - 1) * itemsPerPage) + 1}</span> to <span className="font-bold text-gray-900">{Math.min(currentPage * itemsPerPage, filteredData.length)}</span> of{' '}
                    <span className="font-bold text-red-600">{filteredData.length}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-full shadow-sm bg-white p-1 ring-1 ring-gray-200" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center justify-center h-8 w-8 rounded-full bg-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
                    >
                      <span className="sr-only">Previous</span>
                      <ChevronLeftIcon className="h-4 w-4 font-bold" aria-hidden="true" />
                    </button>
                    <div className="flex items-center px-4">
                      <span className="text-sm font-semibold text-gray-700">
                        Page {currentPage} of {totalPages}
                      </span>
                    </div>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center justify-center h-8 w-8 rounded-full bg-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
                    >
                      <span className="sr-only">Next</span>
                      <ChevronRightIcon className="h-4 w-4 font-bold" aria-hidden="true" />
                    </button>
                  </nav>
                </div>
              </div>
              
              {/* Mobile standard pagination */}
              <div className="flex items-center justify-between w-full sm:hidden">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-5 py-2.5 border border-gray-200 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 shadow-sm disabled:opacity-40 transition-all"
                >
                  Previous
                </button>
                <span className="text-sm font-bold text-gray-600">
                  {currentPage} / {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-5 py-2.5 border border-gray-200 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 shadow-sm disabled:opacity-40 transition-all"
                >
                  Next
                </button>
              </div>
            </>
          )}

          {paginationMode === 'infinite' && (
            <div className="w-full flex justify-center py-2">
              {currentPage < totalPages ? (
                <button
                  onClick={() => setCurrentPage(p => p + 1)}
                  className="px-8 py-3 ring-1 ring-red-200 text-sm font-bold rounded-full text-red-600 bg-red-50 hover:bg-red-600 hover:text-white shadow-sm transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-md"
                >
                  Load More Results...
                </button>
              ) : (
                <span className="text-sm text-gray-400 font-bold bg-gray-50 px-6 py-2 rounded-full ring-1 ring-gray-100">All results loaded</span>
              )}
            </div>
          )}

        </div>
      )}
    </div>
  );
};

export default PremiumTable;
